import { cn } from '@/lib/utils';
import { Avatar, Rating, Tooltip } from '@mui/material';
import { AE, AU, CY, CZ, ES, GB, IL, NZ, US, VC } from 'country-flag-icons/react/3x2';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TbChevronDown, TbChevronUp, TbSelector, TbStarFilled } from 'react-icons/tb';

import type { ReliabilityResult } from '../data/reliability';
import type { AccountModel, ReliabilityBreakdown } from '../data/types';
import { useDataText } from '../data/use-data-text';
import {
  countryCodeFromHeadquarters,
  faviconUrl,
  formatReviews,
  initials,
  reliabilityColor,
  seniorityTier,
  seniorityYears,
} from './comparator-utils';
import type { SortDir } from './use-sorted-rows';

type FlagComponent = typeof CZ;
const FLAGS: Record<string, FlagComponent> = { AE, AU, CY, CZ, ES, GB, IL, NZ, US, VC };

export function CountryFlag({ headquarters }: { headquarters: string | null }) {
  const dataText = useDataText();
  const code = countryCodeFromHeadquarters(headquarters);
  if (!code) return null;
  const Flag = FLAGS[code];
  if (!Flag) return null;
  return (
    <Flag
      title={headquarters ? dataText(headquarters) : undefined}
      className="w-4 shrink-0 rounded-[1px]"
    />
  );
}

export function FirmLogo({
  name,
  website,
  logoUrl,
  size = 30,
}: {
  name: string;
  website: string;
  logoUrl: string | null;
  size?: number;
}) {
  const src = logoUrl ?? (faviconUrl(website) || undefined);
  return (
    <Avatar
      variant="rounded"
      src={src}
      alt={name}
      sx={{
        width: size,
        height: size,
        bgcolor: 'rgba(255,255,255,0.06)',
        color: '#a3a3a3',
        fontSize: size * 0.4,
        fontWeight: 600,
      }}
    >
      {initials(name)}
    </Avatar>
  );
}

export function RatingStars({
  score,
  reviews,
}: {
  score: number | null;
  reviews: number | null;
}) {
  const { t } = useTranslation('propfirms');
  if (score == null) {
    return <span className="text-[11px] text-neutral-500">{t('rating.unrated')}</span>;
  }
  return (
    <div className="flex items-center gap-1.5">
      <Rating
        value={score}
        precision={0.5}
        readOnly
        size="small"
        sx={{
          fontSize: 14,
          color: '#f5b301',
          '& .MuiRating-iconEmpty': { color: 'rgba(255,255,255,0.18)' },
        }}
      />
      <span className="text-[11px] tabular-nums text-neutral-200">{score.toFixed(1)}</span>
      {reviews != null && (
        <span className="text-[11px] tabular-nums text-neutral-500 md:text-[10px]">
          ({formatReviews(reviews)})
        </span>
      )}
    </div>
  );
}

const SENIORITY_CLASSES: Record<ReturnType<typeof seniorityTier>, string> = {
  red: 'bg-red-500/15 text-red-400',
  orange: 'bg-orange-500/15 text-orange-400',
  yellow: 'bg-amber-400/15 text-amber-300',
  green: 'bg-green-500/15 text-green-400',
};

export function SeniorityBadge({ foundedYear }: { foundedYear: number | null }) {
  const { t } = useTranslation('propfirms');
  const years = seniorityYears(foundedYear);
  if (years == null) {
    return <span className="text-[11px] text-neutral-500">{t('value.none')}</span>;
  }
  return (
    <span
      title={t('seniority.since', { year: foundedYear })}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
        SENIORITY_CLASSES[seniorityTier(years)],
      )}
    >
      {t('seniority.years', { count: years })}
    </span>
  );
}

function ReliabilityBreakdownTooltip({ breakdown }: { breakdown: ReliabilityBreakdown }) {
  const { t } = useTranslation('propfirms');
  const rows: [string, number][] = [
    [t('reliability.regulation'), breakdown.regulation],
    [t('reliability.seniority'), breakdown.seniority],
    [t('reliability.reviews'), breakdown.reviews],
    [t('reliability.transparency'), breakdown.transparency],
  ];
  return (
    <div className="flex flex-col gap-0.5 py-1 text-[11px]">
      {rows.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-4">
          <span className="text-neutral-400">{label}</span>
          <span className="tabular-nums">{value}</span>
        </div>
      ))}
    </div>
  );
}

export function ReliabilityScore({ result }: { result: ReliabilityResult }) {
  return (
    <Tooltip title={<ReliabilityBreakdownTooltip breakdown={result.breakdown} />} arrow>
      <span
        className="inline-flex cursor-default items-center gap-0.5 text-[12px] font-semibold tabular-nums"
        style={{ color: reliabilityColor(result.score) }}
      >
        {result.score}
        <span className="text-[11px] font-normal text-neutral-500 md:text-[9px]">
          /100
        </span>
      </span>
    </Tooltip>
  );
}

export const MODEL_META = {
  '1-step': { short: '1', key: 'model.oneStep' },
  '2-step': { short: '2', key: 'model.twoStep' },
  '3-step': { short: '3', key: 'model.threeStep' },
  instant: { short: 'Inst', key: 'model.instant' },
} as const satisfies Record<AccountModel, { short: string; key: string }>;

export function ModelChips({
  models,
  className,
}: {
  models: readonly AccountModel[];
  className?: string;
}) {
  const { t } = useTranslation('propfirms');
  return (
    <div className={cn('flex flex-wrap justify-center gap-1', className)}>
      {models.map((m) => (
        <Tooltip key={m} title={t(MODEL_META[m].key)}>
          <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[11px] font-medium text-neutral-300 md:text-[10px]">
            {MODEL_META[m].short}
          </span>
        </Tooltip>
      ))}
    </div>
  );
}

export function RegulatorBadges({ regulation }: { regulation: readonly string[] }) {
  const { t } = useTranslation('propfirms');
  if (regulation.length === 0) {
    return <span className="text-[11px] text-neutral-500">{t('value.none')}</span>;
  }
  const shown = regulation.slice(0, 4);
  const extra = regulation.slice(4);
  return (
    <div className="flex flex-wrap justify-center gap-1">
      {shown.map((entry) => (
        <span
          key={entry}
          title={entry}
          className="rounded border border-primary-500/25 bg-primary-500/15 px-1.5 py-0.5 text-[11px] font-medium text-primary-300 md:text-[10px]"
        >
          {entry.split('(')[0].trim()}
        </span>
      ))}
      {extra.length > 0 && (
        <Tooltip title={extra.join(', ')}>
          <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[11px] font-medium text-neutral-400 md:text-[10px]">
            +{extra.length}
          </span>
        </Tooltip>
      )}
    </div>
  );
}

export function SortableHeaderCell({
  label,
  sortKey,
  active,
  dir,
  onSort,
}: {
  label: string;
  sortKey: string;
  active: boolean;
  dir: SortDir;
  onSort: (key: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={cn(
        // Le reset de Tailwind coupe l'héritage de `uppercase` sur un bouton :
        // ces en-têtes juraient avec leurs voisins.
        'flex items-center justify-center gap-1 uppercase transition-colors hover:text-neutral-200',
        active ? 'text-neutral-100' : 'text-neutral-500',
      )}
    >
      <span>{label}</span>
      {active ? (
        dir === 'asc' ? (
          <TbChevronUp size={12} />
        ) : (
          <TbChevronDown size={12} />
        )
      ) : (
        <TbSelector size={12} className="opacity-40" />
      )}
    </button>
  );
}

export interface MobileStat {
  label: string;
  value: ReactNode;
  className?: string;
}

/// Une enseigne par rangée de 56 px sous md : logo, nom, note en une étoile,
/// puis trois chiffres sur une seule ligne. La carte à libellés empilés faisait
/// 110 à 150 px par enseigne, dont deux cadres l'un dans l'autre et un levier
/// sur trois lignes ; la fiche détaillée, ouverte d'un toucher, garde le reste.
export function ComparatorMobileRow({
  name,
  website,
  logoUrl,
  headquarters,
  score,
  reviews,
  stats,
  onClick,
}: {
  name: string;
  website: string;
  logoUrl: string | null;
  headquarters: string | null;
  score: number | null;
  reviews: number | null;
  stats: readonly MobileStat[];
  onClick?: () => void;
}) {
  const { t } = useTranslation('propfirms');
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      className={cn(
        'flex h-14 items-center gap-2.5 border-b border-white/[0.05] px-4 md:hidden',
        onClick && 'cursor-pointer active:bg-[#131318]',
      )}
    >
      <FirmLogo name={name} website={website} logoUrl={logoUrl} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-medium text-neutral-100">
            {name}
          </span>
          <CountryFlag headquarters={headquarters} />
          <span className="ml-auto flex shrink-0 items-center gap-1 text-[11px] tabular-nums">
            {score == null ? (
              <span className="text-neutral-500">{t('rating.unrated')}</span>
            ) : (
              <>
                <TbStarFilled size={12} className="text-[#f5b301]" />
                <span className="text-neutral-200">{score.toFixed(1)}</span>
                {reviews != null && (
                  <span className="text-neutral-500">({formatReviews(reviews)})</span>
                )}
              </>
            )}
          </span>
        </div>
        <div className="mt-0.5 flex items-baseline gap-x-3 overflow-hidden whitespace-nowrap text-[11px] leading-4">
          {stats.map((s) => (
            <span
              key={s.label}
              className="shrink-0 last:min-w-0 last:shrink last:truncate"
            >
              <span className="text-neutral-500">{s.label} </span>
              <span className={cn('font-medium tabular-nums text-neutral-200', s.className)}>
                {s.value}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
